// Draws the meeting grid onto a canvas so MediaRecorder has something to encode.
//
// The important decision here is WHERE THE PIXELS COME FROM.
//
// Every visible tile is already decoding and painting its stream. Drawing from
// those exact elements costs a texture copy and no decode. The obvious
// alternative — a fresh <video> per remote stream, fed from the MediaStreams —
// would decode every participant a SECOND time, for the whole recording, on a
// machine that is already encoding. That is the cost this feature exists to
// avoid, so tiles are located in the DOM by `data-tile-id` instead.
//
// Reading on-screen elements pins the draw loop to the main thread: the only
// route into a worker is MediaStreamTrackProcessor, which means decoding
// everything twice again. Capped fps on the main thread is the better trade.

import { getAvatarColor } from '@vc/utils/avatarColors'
import { computeRowDistribution } from '@vc/views/vcLayout/components/video/tile-geometry'

/** What to draw, in grid order. Mirrors the tiles the recorder can see. */
export type CompositorTile = {
  /** Matches `data-tile-id` on the tile's visible element. */
  id: string
  name: string
  /** False → avatar placeholder instead of a black rectangle. */
  videoOn: boolean
  /** Background image URL when a virtual background is active on this tile. */
  backgroundUrl?: string | null
}

const GAP = 8
const BG = '#202124'

/** Same initials rule as the tiles, so a camera-off peer looks familiar. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  if (parts[0]) return parts[0][0].toUpperCase()
  return 'U'
}

/**
 * `object-fit: cover` for drawImage — fills the cell and crops the overflow,
 * matching how the tiles themselves render. Letterboxing here instead would make
 * the recording look nothing like the meeting.
 */
function drawCover(
  ctx: CanvasRenderingContext2D,
  src: CanvasImageSource,
  sw: number,
  sh: number,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
) {
  if (!sw || !sh) return
  const scale = Math.max(dw / sw, dh / sh)
  const w = sw * scale
  const h = sh * scale
  ctx.drawImage(src, dx + (dw - w) / 2, dy + (dh - h) / 2, w, h)
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

export type Compositor = {
  /** For the MediaRecorder fallback. The WebCodecs path pulls from `canvas`. */
  readonly stream: MediaStream
  /**
   * The composed surface. The WebCodecs recorder grabs `VideoFrame`s from this
   * on its OWN fixed clock, which is what makes the output constant-frame-rate:
   * `captureStream` only emits when the canvas is redrawn, so any jitter in the
   * draw loop became jitter in the file, and players stutter on that.
   */
  readonly canvas: HTMLCanvasElement
  /** Swap the tile list — peers joining, leaving, cameras toggling. */
  setTiles(tiles: CompositorTile[]): void
  stop(): void
}

export function createCompositor(
  initialTiles: CompositorTile[],
  opts: { width: number; height: number; fps: number },
): Compositor {
  const canvas = document.createElement('canvas')
  canvas.width = opts.width
  canvas.height = opts.height
  const ctx = canvas.getContext('2d', { alpha: false })!

  let tiles = initialTiles
  let running = true
  let rafId = 0
  let timerId: ReturnType<typeof setTimeout> | null = null

  // Element lookups and text metrics are per-tile constants, not per-frame ones.
  // Doing them inside the draw loop meant N querySelector + N measureText calls
  // every frame, which is pure overhead on the thread the call itself needs.
  let elementCache = new Map<string, Element | null>()
  const textWidthCache = new Map<string, number>()

  const tileElement = (id: string): HTMLVideoElement | HTMLCanvasElement | null => {
    if (!elementCache.has(id)) {
      elementCache.set(id, document.querySelector(`[data-tile-id="${CSS.escape(id)}"]`))
    }
    const el = elementCache.get(id) ?? null
    // A cached node that has since been unmounted (pagination, layout switch)
    // would draw a stale last frame forever.
    if (el && !el.isConnected) {
      elementCache.delete(id)
      return null
    }
    return el as HTMLVideoElement | HTMLCanvasElement | null
  }

  // Virtual-background tiles render as a CSS background image with the MediaPipe
  // cutout canvas on top, so the background has to be drawn separately or the
  // person appears against black.
  const bgCache = new Map<string, HTMLImageElement>()
  const backgroundFor = (url: string): HTMLImageElement | null => {
    const hit = bgCache.get(url)
    if (hit) return hit.complete && hit.naturalWidth > 0 ? hit : null
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.src = url
    bgCache.set(url, img)
    return null
  }

  const drawTile = (tile: CompositorTile, x: number, y: number, w: number, h: number) => {
    ctx.save()
    roundRect(ctx, x, y, w, h, 10)
    ctx.clip()

    ctx.fillStyle = '#3c4043'
    ctx.fillRect(x, y, w, h)

    const el = tile.videoOn ? tileElement(tile.id) : null

    if (el) {
      if (tile.backgroundUrl) {
        const bg = backgroundFor(tile.backgroundUrl)
        if (bg) drawCover(ctx, bg, bg.naturalWidth, bg.naturalHeight, x, y, w, h)
      }
      const sw = el instanceof HTMLVideoElement ? el.videoWidth : el.width
      const sh = el instanceof HTMLVideoElement ? el.videoHeight : el.height
      // A <video> that hasn't produced a frame yet reports 0×0 — drawing it
      // throws in some browsers and paints garbage in others.
      if (sw > 0 && sh > 0) drawCover(ctx, el, sw, sh, x, y, w, h)
      else drawAvatar(tile, x, y, w, h)
    } else {
      drawAvatar(tile, x, y, w, h)
    }

    drawNameplate(tile, x, y, w, h)
    ctx.restore()
  }

  const drawAvatar = (tile: CompositorTile, x: number, y: number, w: number, h: number) => {
    ctx.fillStyle = getAvatarColor(tile.name)
    ctx.fillRect(x, y, w, h)
    const r = Math.min(w, h) * 0.18
    ctx.beginPath()
    ctx.arc(x + w / 2, y + h / 2, r, 0, Math.PI * 2)
    ctx.fillStyle = 'rgba(255,255,255,0.3)'
    ctx.fill()
    ctx.fillStyle = '#202124'
    ctx.font = `600 ${Math.round(r * 0.8)}px system-ui, sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(initialsOf(tile.name), x + w / 2, y + h / 2)
  }

  const drawNameplate = (tile: CompositorTile, x: number, y: number, w: number, h: number) => {
    const fontSize = Math.max(11, Math.round(h * 0.055))
    ctx.font = `500 ${fontSize}px system-ui, sans-serif`
    ctx.textAlign = 'left'
    ctx.textBaseline = 'middle'
    const padX = 8
    const plateH = fontSize + 10
    const cacheKey = `${tile.name}@${fontSize}`
    let measured = textWidthCache.get(cacheKey)
    if (measured === undefined) {
      measured = ctx.measureText(tile.name).width
      textWidthCache.set(cacheKey, measured)
    }
    const textW = Math.min(measured, w - padX * 4)
    ctx.fillStyle = 'rgba(0,0,0,0.5)'
    roundRect(ctx, x + padX, y + h - plateH - padX, textW + padX * 2, plateH, 6)
    ctx.fill()
    ctx.fillStyle = '#fff'
    ctx.save()
    ctx.beginPath()
    ctx.rect(x + padX * 2, y + h - plateH - padX, textW, plateH)
    ctx.clip()
    ctx.fillText(tile.name, x + padX * 2, y + h - plateH / 2 - padX)
    ctx.restore()
  }

  const drawFrame = () => {
    ctx.fillStyle = BG
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    const current = tiles
    if (current.length > 0) {
      // Same maths the on-screen grid uses, so the recording is laid out the way
      // the recorder saw it rather than in some parallel scheme.
      const rows = computeRowDistribution(current.length, canvas.width, canvas.height)
      const rowH = (canvas.height - GAP * (rows.length - 1)) / rows.length
      let index = 0
      let y = 0
      for (const perRow of rows) {
        const tileW = (canvas.width - GAP * (perRow - 1)) / perRow
        let x = 0
        for (let i = 0; i < perRow && index < current.length; i++, index++) {
          drawTile(current[index], x, y, tileW, rowH)
          x += tileW + GAP
        }
        y += rowH + GAP
      }
    }
  }

  // ONE frame in flight at a time.
  //
  // The first version fired a setTimeout every frameInterval and queued a fresh
  // requestAnimationFrame each time, without waiting for the previous frame. On
  // a busy machine the rAFs backed up, the browser ran the whole backlog in a
  // burst, and that burst starved the thread further — a feedback loop that
  // eventually blocked the main thread long enough to miss Phoenix heartbeats
  // (peer drops off the room) and stop painting the tiles we sample (remote
  // video appears frozen). Never schedule the next frame before this one is done.
  const targetInterval = 1000 / opts.fps
  // Backs off when frames are expensive. A recording must never be the reason a
  // call degrades: if compositing starts costing a large share of the frame
  // budget we drop the capture rate rather than fight the meeting for the
  // thread. Recovers as soon as frames get cheap again.
  // Floor kept close to the target on purpose. canvas.captureStream only emits a
  // frame when the canvas is redrawn, so backing the draw rate off makes the
  // OUTPUT variable-frame-rate — and VFR is what players stutter on. Protecting
  // the call still wins, but the swing stays narrow.
  const MAX_INTERVAL = 1000 / 12
  let frameInterval = targetInterval
  let lastFrameAt = 0
  let slowFrames = 0
  let fastFrames = 0

  const renderFrame = (now: number) => {
    if (!running) return
    lastFrameAt = now
    const startedAt = performance.now()
    drawFrame()
    const cost = performance.now() - startedAt

    // Over half the frame budget spent drawing means we are crowding out
    // everything else on the thread.
    if (cost > frameInterval * 0.5) {
      fastFrames = 0
      if (++slowFrames >= 5 && frameInterval < MAX_INTERVAL) {
        frameInterval = Math.min(MAX_INTERVAL, frameInterval * 1.5)
        slowFrames = 0
      }
    } else if (cost < frameInterval * 0.2) {
      slowFrames = 0
      if (++fastFrames >= 60 && frameInterval > targetInterval) {
        frameInterval = Math.max(targetInterval, frameInterval / 1.5)
        fastFrames = 0
      }
    }
    schedule()
  }

  function schedule() {
    if (!running) return
    // A hidden tab throttles rAF to ~1fps and stops painting the <video>
    // elements we sample, so fall back to a timer there.
    if (document.visibilityState !== 'visible') {
      timerId = setTimeout(() => renderFrame(performance.now()), frameInterval)
      return
    }
    rafId = requestAnimationFrame((now) => {
      if (!running) return
      // rAF runs at display rate (often 60Hz+); only draw when a frame is due.
      // Skipping is the point — if the thread is busy, we fall behind rather
      // than piling up work.
      if (now - lastFrameAt + 1 < frameInterval) {
        schedule()
        return
      }
      renderFrame(now)
    })
  }

  drawFrame()
  schedule()

  return {
    stream: canvas.captureStream(opts.fps),
    canvas,
    setTiles(next) {
      tiles = next
      // Tiles changed — re-resolve elements rather than trusting stale nodes.
      elementCache = new Map()
    },
    stop() {
      running = false
      if (timerId) clearTimeout(timerId)
      cancelAnimationFrame(rafId)
    },
  }
}
